import React from 'react';

const ProfessionalResumePreview = ({ componentRef, resumeData = {} }) => {
  const {
    full_name,
    job_title,
    email,
    phone,
    location,
    websites,
    projects,
    professional_summary,
    relevant_experience,
    education,
    certifications,
    achievements,
    skills = {},
    key_skills = []
  } = resumeData;

  const {
    programming_languages = [],
    frameworks = [],
    tools = [],
    ai_ml_technologies = []
  } = skills || {};

  const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
  
  // Enhanced logic to detect when to show user data
  const shouldShowUserData = resumeData && (
      !resumeData.isTemplate || 
      resumeData.full_name || 
      resumeData.email || 
      resumeData.phone || 
      resumeData.location ||
      resumeData.websites ||
      resumeData.professional_summary ||
      resumeData.projects ||
      resumeData.education ||
      resumeData.experience ||
      resumeData.skills ||
      resumeData.key_skills ||
      resumeData.achievements ||
      resumeData.certifications
  );

  const displaySkills = key_skills.length > 0 || 
    (programming_languages.length > 0 || frameworks.length > 0 || tools.length > 0 || ai_ml_technologies.length > 0);

  // Skill levels for progress bars
  const skillLevels = {
    'Lesson Planning': 90,
    'Hands-on Experiments': 80,
    'Differentiated Instruction': 85,
    'Technology Integration': 70,
    'Collaborative Learning': 88,
    'Classroom Management': 85,
    'Assessment Strategies': 95,
    'Curriculum Development': 80,
    'Parent Communication': 75
  };

  // Soft skills with dot ratings
  const softSkills = {
    'Communication': 4,
    'Adaptability': 3,
    'Patience': 5,
    'Creativity': 4
  };

  const renderProgressBar = (skill, level) => {
    const totalSegments = 10;
    const filledSegments = Math.round((level / 100) * totalSegments);
    return (
      <div key={skill} className="skill-item-container">
        <div className="skill-name-text">{skill}</div>
        <div className="progress-bar-wrapper">
          {Array.from({ length: totalSegments }).map((_, i) => (
            <div
              key={i}
              className={`progress-segment ${
                i < filledSegments ? 'filled' : 'empty'
              }`}
            ></div>
          ))}
        </div>
      </div>
    );
  };

  const renderDotRating = (skill, rating) => (
    <div key={skill} className="soft-skill-item-container">
      <span className="soft-skill-name-text">{skill}</span>
      <div className="dot-rating-wrapper">
        {[1, 2, 3, 4, 5].map(dot => (
          <div
            key={dot}
            className={`dot ${
              dot <= rating ? 'filled' : 'empty'
            }`}
          ></div>
        ))}
      </div>
    </div>
  );

  // Split skills into two columns for rendering
  const skillEntries = Object.entries(skillLevels);
  const skillsCol1 = skillEntries.slice(0, 5); // First 5 skills for left column
  const skillsCol2 = skillEntries.slice(5);    // Remaining skills for right column

  // Split soft skills into two columns for rendering
  const softSkillEntries = Object.entries(softSkills);
  const softSkillsCol1 = softSkillEntries.slice(0, 2); // First 2 soft skills
  const softSkillsCol2 = softSkillEntries.slice(2);    // Remaining 2 soft skills

  return (
    <div id="section-to-print" ref={componentRef} className="resume-container">
      {/* Header Section */}
      <div className="header-section">
        <div className="header-text-content">
          <h1 className="header-name">
            {isUploadedResume ? full_name : "Benjamin Anderson"}
          </h1>
          <h2 className="header-title">
            {shouldShowUserData && job_title ? job_title : "Elementary Science Teacher"}
          </h2>
          
          <div className="contact-info-grid">
            <div className="contact-item">
              <span className="contact-icon">✉</span>
              <span>{isUploadedResume ? email : "support@resumedesign.ai"}</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📞</span>
              <span>{isUploadedResume ? phone : "(555) 345-6789"}</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">🏠</span>
              <span>{isUploadedResume ? location : "New York, US"}</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">💼</span>
              <span>LinkedIn.com</span>
            </div>
          </div>
        </div>
        
        {/* Profile Photo */}
        <div className="profile-photo-container">
          <img 
            src="/professional-headshot.png"
            alt="Profile"
            className="profile-photo"
          />
        </div>
      </div>

      <div className="main-content">
        {/* Professional Summary */}
        <section className="section-container">
          <div className="section-heading-col">
            <h3 className="section-title">Professional Summary</h3>
          </div>
          <div className="section-content-col">
            <p className="summary-text">
              {isUploadedResume && professional_summary ? professional_summary :
                "Passionate Elementary Science Teacher with 12 years of experience creating engaging and interactive lessons that foster a love for learning in students. Recognized for implementing innovative teaching methods that resulted in a 20% increase in standardized test scores. Skilled in developing hands-on experiments and projects that enhance student understanding of scientific concepts. Committed to creating a positive and inclusive classroom environment where all students can thrive and reach their full potential."
              }
            </p>
          </div>
        </section>

        {/* Work Experience */}
        <section className="section-container">
          <div className="section-heading-col">
            <h3 className="section-title">Work Experience</h3>
          </div>
          <div className="section-content-col">
            {isUploadedResume && relevant_experience && relevant_experience.length > 0 ? (
              relevant_experience.map((exp, i) => (
                <div key={i} className="experience-item">
                  <div className="experience-header">
                    <div>
                      <h4 className="job-title">{exp.position}</h4>
                      <p className="company-name">{exp.company}</p>
                    </div>
                    <span className="experience-date">
                      {exp.start_date} - {exp.end_date}
                    </span>
                  </div>
                  <ul className="experience-list">
                    {(exp.description_points || []).map((point, j) => (
                      <li key={j}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <>
                <div className="experience-item">
                  <div className="experience-header">
                    <div>
                      <h4 className="job-title">Lead Elementary Science Teacher</h4>
                      <p className="company-name">Sunshine Elementary School</p>
                    </div>
                    <span className="experience-date">Aug 2008 - May 2016</span>
                  </div>
                  <ul className="experience-list">
                    <li>Developed and implemented engaging science curriculum for elementary students</li>
                    <li>Utilized hands-on experiments and activities to enhance learning experiences</li>
                    <li>Collaborated with colleagues to align science curriculum with state standards</li>
                    <li>Organized and led science fairs and field trips to promote student interest in STEM subjects</li>
                    <li>Implemented differentiated instruction to meet the needs of diverse learners</li>
                  </ul>
                </div>

                <div className="experience-item">
                  <div className="experience-header">
                    <div>
                      <h4 className="job-title">Elementary Science Teacher</h4>
                      <p className="company-name">Rainbow Elementary School</p>
                    </div>
                    <span className="experience-date">Aug 2016 - Present</span>
                  </div>
                  <ul className="experience-list">
                    <li>Design and deliver engaging science lessons for elementary students</li>
                    <li>Integrate technology and multimedia resources into science curriculum</li>
                    <li>Assess student understanding through formative and summative assessments</li>
                    <li>Participate in professional development workshops to enhance teaching practices</li>
                    <li>Maintain a positive classroom environment that fosters inquiry and curiosity</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Education */}
        <section className="section-container">
          <div className="section-heading-col">
            <h3 className="section-title">Education</h3>
          </div>
          <div className="section-content-col">
            {isUploadedResume && education && education.length > 0 ? (
              education.map((edu, i) => (
                <div key={i} className="education-item">
                  <div className="education-header">
                    <div>
                      <h4 className="degree-title">
                        {edu.degree} in {edu.field_of_study}
                      </h4>
                      <p className="institution-name">{edu.institute || edu.institution}</p>
                      {edu.key_coursework && (
                        <p className="coursework-text">
                          {edu.key_coursework}
                        </p>
                      )}
                    </div>
                    <span className="education-date">
                      {edu.graduation_date || edu.end_year}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="education-item">
                  <div className="education-header">
                    <div>
                      <h4 className="degree-title">Masters in Elementary Education</h4>
                      <p className="institution-name">State University</p>
                      <p className="coursework-text">
                        Focused on advanced teaching methods and curriculum development for elementary science education.
                      </p>
                    </div>
                    <span className="education-date">Sep 2006 - May 2008</span>
                  </div>
                </div>

                <div className="education-item">
                  <div className="education-header">
                    <div>
                      <h4 className="degree-title">Bachelor of Science in Elementary Education</h4>
                      <p className="institution-name">State University</p>
                      <p className="coursework-text">
                        Studied foundational theories of education and specialized in science instruction for elementary students.
                      </p>
                    </div>
                    <span className="education-date">Sep 2002 - May 2006</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Skills Section */}
        <section className="section-container">
          <div className="section-heading-col">
            <h3 className="section-title">Skills</h3>
          </div>
          <div className="section-content-col skills-grid-container">
            <div className="skills-column">
              {displaySkills && key_skills.length > 0 ? (
                key_skills.slice(0, Math.ceil(key_skills.length / 2)).map((skill, index) => 
                  renderProgressBar(skill, Math.floor(Math.random() * 30) + 70)
                )
              ) : (
                skillsCol1.map(([skill, level]) => 
                  renderProgressBar(skill, level)
                )
              )}
            </div>
            <div className="skills-column">
              {displaySkills && key_skills.length > 0 ? (
                key_skills.slice(Math.ceil(key_skills.length / 2)).map((skill, index) => 
                  renderProgressBar(skill, Math.floor(Math.random() * 30) + 70)
                )
              ) : (
                skillsCol2.map(([skill, level]) => 
                  renderProgressBar(skill, level)
                )
              )}
            </div>
          </div>
        </section>

        {/* Soft Skills Section */}
        <section className="section-container">
          <div className="section-heading-col">
            <h3 className="section-title">Soft Skills</h3>
          </div>
          <div className="section-content-col soft-skills-grid-container">
            <div className="soft-skills-column">
              {softSkillsCol1.map(([skill, rating]) => 
                renderDotRating(skill, rating)
              )}
            </div>
            <div className="soft-skills-column">
              {softSkillsCol2.map(([skill, rating]) => 
                renderDotRating(skill, rating)
              )}
            </div>
          </div>
        </section>

        {/* Projects Section - if needed */}
        {projects && projects.length > 0 && (
          <section className="section-container">
            <div className="section-heading-col">
              <h3 className="section-title">Projects</h3>
            </div>
            <div className="section-content-col">
              {projects.map((proj, i) => (
                <div key={i} className="project-item">
                  <h4 className="project-title">{proj.title}</h4>
                  <p className="project-description">
                    {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                  </p>
                  {proj.tech_stack && (
                    <p className="project-tech">
                      Technologies: {proj.tech_stack.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProfessionalResumePreview;
